import React, { useRef, useState } from 'react';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Pagination, Navigation, Autoplay } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/pagination';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import gsap from 'gsap';
import SwiperCore from 'swiper';

import ProtectionIcon from "@/assets/features/protection.png";
import ShippingIcon from "@/assets/features/shipping.png";
import ClaimsIcon from "@/assets/features/claims.png";
import FraudIcon from "@/assets/features/fraud.png";
import ReturnsIcon from "@/assets/features/returns.png";

import ProtectionCover from "@/assets/images/protectionOverviewCover.png";
import ShippingCover from "@/assets/images/shippingOverviewCover.png";
import ClaimsCover from "@/assets/images/claimsOverviewCover.png";
import FraudCover from "@/assets/images/fraudOverviewCover.png";
import ReturnsCover from "@/assets/images/returnsOverviewCover.png";

const covers = [
    ProtectionCover,
    ShippingCover,
    ClaimsCover,
    FraudCover,
    ReturnsCover,
];

const navItems = [
    { icon: ProtectionIcon, label: 'Protection' },
    { icon: ShippingIcon, label: 'Shipping' },
    { icon: ClaimsIcon, label: 'Claims' },
    { icon: FraudIcon, label: 'Fraud' },
    { icon: ReturnsIcon, label: 'Returns' },
];

const underlineColors = [
    '#00C5FB',
    '#00FFBD',
    '#FFBA33',
    '#FF3333',
    '#D357FF',
];

const WhyProtega: React.FC = () => {
    const swiperRef = useRef<SwiperCore>();
    const slideContainerRef = useRef<HTMLDivElement>(null);
    const [activeIndex, setActiveIndex] = useState(0);
    const count = navItems.length;
    const segmentWidth = 100 / count;

    const fadeSlide = () => {
        if (!slideContainerRef.current) return;
        gsap
            .timeline()
            .to(slideContainerRef.current, { autoAlpha: 0, duration: 0.25 })
            .to(slideContainerRef.current, { autoAlpha: 1, duration: 0.25 });
    };

    const goTo = (idx: number) => {
        setActiveIndex(idx);
        swiperRef.current?.slideToLoop(idx);
    };

    return (
        <section className="tw-bg-black tw-text-white tw-py-24 tw-px-4 md:tw-px-12">
            <div className="tw-max-w-7xl tw-mx-auto">
                {/* Top Copy */}
                <div className="tw-text-start tw-mx-2 lg:tw-mx-6 xl:tw-mx-52 tw-mb-16">
                    <div className="tw-text-base sm:tw-text-xl tw-font-semibold tw-text-gray-400 tw-uppercase tw-mb-2">
                        Why Protega
                    </div>
                    <h2 className="tw-text-xl sm:tw-text-2xl md:tw-text-4xl lg:tw-text-5xl tw-font-extrabold tw-leading-tight">
                        Increase margins, streamline<br />
                        customer interactions, and reduce<br />
                        post‑purchase fraud.
                    </h2>
                    <p className="tw-text-sm sm:tw-text-lg tw-text-gray-400 tw-mt-4 tw-leading-relaxed">
                        Automate your claims experience, boost customer loyalty, and lower operational overhead<br />
                        with a white‑labeled, self‑service experience. Customers can view order status and tracking,<br />
                        file claims related to shipping or product issues, initiate returns and exchanges, and receive<br />
                        immediate resolutions. All in one place.
                    </p>
                </div>

                {/* Slider */}
                <div
                    style={{ backgroundImage: "url('/overview/platformBg.png')", backgroundSize: 'cover' }}>
                    <div ref={slideContainerRef} className="tw-relative tw-mb-12 tw-px-2">
                        <button className="custom-swiper-prev tw-hidden md:tw-flex tw-absolute tw-top-1/2 tw-left-0 -tw-translate-y-1/2 tw-z-10 tw-w-10 tw-h-10 tw-items-center tw-justify-center hover:tw-text-[#00C5FB]">
                            <ChevronLeft size={40} />
                        </button>
                        <button className="custom-swiper-next tw-hidden md:tw-flex tw-absolute tw-top-1/2 tw-right-0 -tw-translate-y-1/2 tw-z-10 tw-w-10 tw-h-10 tw-items-center tw-justify-center hover:tw-text-[#00C5FB]">
                            <ChevronRight size={40} />
                        </button>
                        <Swiper
                            modules={[Navigation, Pagination, Autoplay]}
                            loop
                            slidesPerView={1}
                            navigation={{ prevEl: '.custom-swiper-prev', nextEl: '.custom-swiper-next' }}
                            pagination={{ el: '.custom-pagination', clickable: true }}
                            onSwiper={s => (swiperRef.current = s)}
                            autoplay={{
                                delay: 8000,
                                disableOnInteraction: false,
                            }}
                            onSlideChange={s => {
                                setActiveIndex(s.realIndex);
                                fadeSlide();
                            }}
                        >
                            {covers.map((src, i) => (
                                <SwiperSlide key={i}>
                                    <div
                                        className="tw-w-full tw-h-[400px] md:tw-h-[550px] tw-grid tw-place-content-center tw-overflow-hidden tw-rounded-2xl">
                                        <img
                                            src={src}
                                            alt={`Cover ${i}`}
                                            className="tw-w-full tw-h-full tw-object-contain"
                                        />
                                    </div>
                                </SwiperSlide>
                            ))}
                        </Swiper>

                        <div className="custom-pagination tw-flex tw-space-x-2 tw-justify-center tw-mt-6 md:tw-hidden" />
                    </div>
                </div>

                {/* Nav */}
                <nav className="tw-hidden sm:tw-grid tw-grid-cols-5 tw-max-w-3xl tw-mx-auto tw-relative tw-mt-12">
                    {navItems.map(({ icon, label }, i) => (
                        <button
                            key={i}
                            onClick={() => goTo(i)}
                            className={`tw-flex tw-items-center tw-justify-center tw-space-x-3 tw-py-2 focus:tw-outline-none ${activeIndex !== i ? 'tw-filter tw-grayscale' : ''
                                }`}
                        >
                            <img
                                src={icon}
                                alt={label}
                                className={`tw-h-6 tw-transition-opacity ${activeIndex === i ? 'tw-opacity-100' : 'tw-opacity-50'
                                    }`}
                            />
                            <span
                                className={`tw-text-sm tw-font-medium tw-transition-colors ${activeIndex === i ? 'tw-text-white' : 'tw-text-gray-600'
                                    }`}
                            >
                                {label}
                            </span>
                        </button>
                    ))}
                    <div
                        className="tw-absolute tw-bottom-0 tw-h-[3px] tw-transition-all tw-duration-300 tw-rounded-md"
                        style={{
                            width: `${segmentWidth}%`,
                            left: `${segmentWidth * activeIndex}%`,
                            backgroundColor: underlineColors[activeIndex],
                        }}
                    />
                </nav>
            </div>
        </section>

    );
};

export default WhyProtega;
